import { useAuth } from '@/contexts/AuthContext';
import { useDialog } from '@/contexts/DialogContext';
import { useToast } from '@/contexts/ToastContext';
import { captureSentryError, logSentryEvent } from '@/lib/sentry';
import { buildPaymentReturnUrl, PAYMENT_RETURN_UPGRADE_PATH } from '@/pages/PaymentReturn/paymentReturnFlow';
import { useTranslation } from '@client-app/i18n';
import { billingService, type Plan } from '@common/api/billingService';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import BillingPortalLink from './BillingPortalLink';
import { buildCheckoutReturnUrls } from './paymentUrlUtils';
import PricingCard from './PricingCard';
import {
    PLUGIN_BILLING_CYCLE,
    buildPluginPricingPlanData,
    getPluginPlanFamily,
    getPluginPlanMemberLimit,
    getPluginPlanPrice,
    isPluginCurrentPlan,
    PLUGIN_FALLBACK_PLANS,
    resolvePluginBillingPlans,
    resolvePluginCurrentPlanCode,
} from './pluginPricingCatalog';
import {
    formatScheduledEffectiveDate,
    resolveScheduledPlanBubbleState,
    resolveScheduledPlanButtonState,
    type ScheduledPlanChange,
} from './scheduledPlanChangeUtils';
import {
    resolvePluginPlanCardAction,
    shouldShowPluginScheduledChange,
    type PluginPlanCardAction,
} from './pluginPlanChangeUtils';
import { resolvePluginPlanSubmissionOutcome } from './pluginPlanSubmissionFlow';

interface PluginPlanSelectorProps {
    workspaceId: string;
}

const PluginPlanSelector: React.FC<PluginPlanSelectorProps> = ({ workspaceId }) => {
    const { locale, t } = useTranslation();
    const { showToast } = useToast();
    const { showDialog } = useDialog();
    const { userBilling, refreshWorkspaces } = useAuth();
    const [plans, setPlans] = useState<Plan[]>(PLUGIN_FALLBACK_PLANS);
    const [plansState, setPlansState] = useState<'loading' | 'ready' | 'error'>('loading');
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [scheduledChange, setScheduledChange] = useState<ScheduledPlanChange | null>(null);
    const [pluginSubscriptionPlanId, setPluginSubscriptionPlanId] = useState<string | null>(null);
    const [pluginSubscriptionStatus, setPluginSubscriptionStatus] = useState<string | null>(null);
    const [pluginSubscriptionCurrentPeriodEnd, setPluginSubscriptionCurrentPeriodEnd] = useState<string | null>(null);
    const pluginBillingStatusRequestRef = useRef(0);

    const pluginPlans = resolvePluginBillingPlans(plans);
    // Plugin billing state must come only from the product-scoped endpoint.
    // The shared profile/userBilling state can still contain the Core plan
    // while the product workspace is loading, which would leak Core state into
    // the Plugin pricing cards.
    const subscriptionStatus = pluginSubscriptionStatus;
    const currentPlanCode = resolvePluginCurrentPlanCode(
        pluginPlans.find((plan) => plan.id === pluginSubscriptionPlanId)?.code,
    );
    const isCurrentPlan = subscriptionStatus === 'active';
    const isTrialPlan = subscriptionStatus === 'trial';
    const isRenewablePlan = subscriptionStatus === 'canceled' || subscriptionStatus === 'pending_cancellation';
    const visibleScheduledChange = shouldShowPluginScheduledChange(subscriptionStatus)
        ? scheduledChange
        : null;
    // A subscription scheduled to end is still billable until its period ends.
    // Keep its management entry point available so the owner can resume it.
    const canOpenBillingPortal = isCurrentPlan || isRenewablePlan || isTrialPlan;
    const actionLabel = isCurrentPlan
        ? t('website.pricing.currentPlan')
        : isRenewablePlan
            ? t('website.pricing.pluginRestoreAutoRenewal')
            : t('website.pricing.startSubscription');
    const isActionDisabled = isCurrentPlan
        || isActionLoading
        || (!isRenewablePlan && !canOpenBillingPortal && (plansState === 'loading' || !pluginPlans.length));
    const currentPlan = pluginPlans.find((plan) => isPluginCurrentPlan(plan, currentPlanCode)) ?? null;
    const currentPlanPrice = currentPlan ? getPluginPlanPrice(currentPlan) : null;

    const getPluginPlanActionLabel = (action: PluginPlanCardAction): string => {
        switch (action) {
            case 'current-plan':
                return t('website.pricing.currentPlan');
            case 'upgrade-to-team':
                return t('website.pricing.upgradeToTeam');
            case 'downgrade':
                return t('website.pricing.downgrade');
            case 'renew':
                return t('website.pricing.pluginRestoreAutoRenewal');
            case 'renew-and-switch':
                return t('website.pricing.switchToSolo');
            default:
                return t('website.pricing.startSubscription');
        }
    };

    const getScheduledButtonLabel = (buttonText: string): string => {
        switch (buttonText) {
            case 'Current Plan':
                return t('website.pricing.currentPlan');
            case 'Downgrade scheduled':
                return t('website.pricing.downgradeScheduled');
            case 'Switch to Team':
                return t('website.pricing.switchToTeam');
            default:
                return buttonText;
        }
    };

    useEffect(() => {
        let cancelled = false;

        const loadPluginPlans = async () => {
            if (!workspaceId) {
                setPlans([]);
                setPlansState('error');
                return;
            }

            setPlans(PLUGIN_FALLBACK_PLANS);
            setPlansState('loading');
            try {
                const response = await billingService.getPluginPlans({headers: { 'X-Workspace-Id': workspaceId },});
                if (cancelled) return;
                setPlans(Array.isArray(response) ? response : []);
                setPlansState('ready');
            } catch (error) {
                if (cancelled) return;
                setPlans([]);
                setPlansState('error');
                captureSentryError(error, {
                    area: 'plugin_pricing',
                    flow: 'load_plugin_plans',
                    workspace_id: workspaceId,
                });
            }
        };

        void loadPluginPlans();
        return () => {
            cancelled = true;
        };
    }, [workspaceId]);

    const refreshPluginBillingStatus = useCallback(async () => {
        if (!workspaceId) {
            setScheduledChange(null);
            setPluginSubscriptionPlanId(null);
            setPluginSubscriptionStatus(null);
            setPluginSubscriptionCurrentPeriodEnd(null);
            return null;
        }

        const requestId = ++pluginBillingStatusRequestRef.current;
        try {
            const status = await billingService.getPluginSetupCardStatus({
                headers: { 'X-Workspace-Id': workspaceId },
            });
            if (requestId !== pluginBillingStatusRequestRef.current) {
                return null;
            }

            setScheduledChange(status?.scheduledChange ?? null);
            setPluginSubscriptionPlanId(status?.subscription?.planId || null);
            setPluginSubscriptionStatus(status?.subscription?.status || null);
            setPluginSubscriptionCurrentPeriodEnd(status?.subscription?.currentPeriodEnd || null);
            return status;
        } catch (error) {
            if (requestId === pluginBillingStatusRequestRef.current) {
                setScheduledChange(null);
                setPluginSubscriptionPlanId(null);
                setPluginSubscriptionStatus(null);
                setPluginSubscriptionCurrentPeriodEnd(null);
            }
            captureSentryError(error, {
                area: 'plugin_pricing',
                flow: 'load_scheduled_change',
                workspace_id: workspaceId,
            });
            return null;
        }
    }, [workspaceId]);

    useEffect(() => {
        setPluginSubscriptionPlanId(null);
        setPluginSubscriptionStatus(null);
        setPluginSubscriptionCurrentPeriodEnd(null);
        void refreshPluginBillingStatus();
    }, [refreshPluginBillingStatus]);

    const openBillingPortal = async () => {
        if (isActionLoading) {
            return;
        }

        setIsActionLoading(true);
        try {
            logSentryEvent('payment.portal.requested', {
                area: 'plugin_pricing',
                workspace_id: workspaceId,
            });
            const response = await billingService.createPluginSubscriptionPortal(
                window.location.href,
                { headers: { 'X-Workspace-Id': workspaceId } },
            );
            if (response.paymentType === 'shopify') {
                window.location.reload();
                return;
            }
            if (!response.url) {
                throw new Error('Plugin billing portal URL is missing');
            }
            window.location.href = response.url;
        } catch (error) {
            captureSentryError(error, {
                area: 'plugin_pricing',
                flow: 'billing_portal',
                workspace_id: workspaceId,
            });
            showToast(t('website.pricing.failedToOpenBillingPortal'), 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const startPluginSubscription = async (
        selectedPlan: Plan,
        action: Exclude<PluginPlanCardAction, 'current-plan' | 'renew' | 'renew-and-switch'>,
    ) => {
        if (!selectedPlan) {
            showToast(t('website.pricing.pluginPlanUnavailable'), 'error');
            return;
        }

        const confirmed = await showDialog(action === 'downgrade'
            ? {
                title: t('website.pricing.downgradeToPlanTitle', { plan: selectedPlan.name }),
                subtitle: (
                    <>
                        {t('website.pricing.downgradeToPlanDescriptionPrefix')} <strong className="text-text-main">{selectedPlan.name}</strong>{' '}
                        {t('website.pricing.downgradeToPlanDescriptionSuffix')}
                    </>
                ),
                confirmText: t('website.pricing.confirmChange'),
                cancelText: t('common.cancel'),
            }
            : action === 'upgrade-to-team'
                ? {
                    title: t('website.pricing.upgradeToPlanTitle', { plan: selectedPlan.name }),
                    subtitle: (
                        <>
                            {t('website.pricing.upgradeToPlanDescriptionPrefix')} <strong className="text-text-main">{selectedPlan.name}</strong>{' '}
                            {t('website.pricing.upgradeToPlanDescriptionMiddle', {
                                cycle: t('website.pricing.monthlyLower'),
                            })}{' '}
                            {t('website.pricing.upgradeToPlanDescriptionSuffix')}
                        </>
                    ),
                    confirmText: t('website.pricing.confirmChange'),
                    cancelText: t('common.cancel'),
                }
                : {
                    title: t('website.pricing.activatePaidPlanConfirmTitle', { plan: selectedPlan.name }),
                    subtitle: (
                        <>
                            {t('website.pricing.activatePaidPlanConfirmDescriptionPrefix')} <strong className="text-text-main">{selectedPlan.name}</strong>{' '}
                            {t('website.pricing.activatePaidPlanConfirmDescriptionMiddle', {
                                cycle: t('website.pricing.monthlyLower'),
                            })}{' '}
                            {t('website.pricing.activatePaidPlanConfirmDescriptionSuffix')}
                        </>
                    ),
                    confirmText: t('website.pricing.activatePaidPlan'),
                    cancelText: t('common.cancel'),
                });
        if (!confirmed) return;

        setIsActionLoading(true);
        try {
            const checkoutUrls = buildCheckoutReturnUrls({
                currentUrl: window.location.href,
                mode: 'upgrade',
                planId: selectedPlan.id,
                planCode: selectedPlan.code,
                planName: selectedPlan.name,
                billingCycle: PLUGIN_BILLING_CYCLE,
                workspaceId,
            });
            logSentryEvent('payment.subscription.requested', {
                area: 'plugin_pricing',
                workspace_id: workspaceId,
                plan_id: selectedPlan.id,
                plan_code: selectedPlan.code,
                billing_cycle: PLUGIN_BILLING_CYCLE,
                subscription_status: subscriptionStatus,
                activation_flow: action === 'downgrade'
                    ? 'downgrade'
                    : action === 'upgrade-to-team'
                        ? 'upgrade'
                        : isTrialPlan ? 'trial_to_paid' : 'new_subscription',
            });
            const response = await billingService.createPluginSubscription({
                planId: selectedPlan.id,
                successUrl: checkoutUrls.successUrl,
                cancelUrl: checkoutUrls.cancelUrl,
                axiosRequestConfig: { headers: { 'X-Workspace-Id': workspaceId } },
            });

            const outcome = resolvePluginPlanSubmissionOutcome({ action, response });
            if (outcome.kind === 'scheduled-downgrade') {
                if (outcome.ignoredCheckoutUrl) {
                    logSentryEvent('payment.subscription.unexpected_downgrade_checkout', {
                        area: 'plugin_pricing',
                        workspace_id: workspaceId,
                        plan_id: selectedPlan.id,
                        plan_code: selectedPlan.code,
                    }, 'warn');
                }

                await refreshPluginBillingStatus();
                showToast(t('website.pricing.planChangeScheduledToast'), 'success');
                return;
            }

            if (outcome.kind === 'checkout') {
                window.location.href = outcome.paymentUrl;
                return;
            }

            if (outcome.kind === 'direct-subscription-success') {
                window.location.href = buildPaymentReturnUrl({
                    origin: window.location.origin,
                    kind: 'subscription',
                    result: 'success',
                    mode: 'upgrade',
                    returnPath: PAYMENT_RETURN_UPGRADE_PATH,
                    workspaceId,
                    planId: selectedPlan.id,
                    planCode: selectedPlan.code,
                    planName: selectedPlan.name,
                    billingCycle: PLUGIN_BILLING_CYCLE,
                });
                return;
            }

            throw new Error('Plugin subscription request did not complete');
        } catch (error) {
            captureSentryError(error, {
                area: 'plugin_pricing',
                flow: 'subscription',
                workspace_id: workspaceId,
                plan_id: selectedPlan.id,
                plan_code: selectedPlan.code,
                billing_cycle: PLUGIN_BILLING_CYCLE,
            });
            showToast(t('website.pricing.failedToStartCheckout'), 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const renewPluginSubscription = async (scheduledPlan?: Plan) => {
        const scheduledPlanEffectiveAt = formatScheduledEffectiveDate(scheduledChange?.effectiveAt, locale);
        const isSwitchingPlan = Boolean(scheduledPlan);
        const confirmed = await showDialog(isSwitchingPlan
            ? {
                title: t('website.pricing.pluginRenewAndSwitchToPlanTitle', { plan: scheduledPlan?.name }),
                subtitle: scheduledPlanEffectiveAt
                    ? t('website.pricing.pluginRenewAndSwitchToPlanDescription', {
                        plan: scheduledPlan?.name,
                        date: scheduledPlanEffectiveAt,
                    })
                    : t('website.pricing.pluginRenewAndSwitchToPlanDescriptionWithoutDate', {
                        plan: scheduledPlan?.name,
                    }),
                confirmText: t('website.pricing.pluginRenewAndSwitchToPlan'),
                cancelText: t('common.cancel'),
            }
            : {
                title: t('website.pricing.pluginRenewSubscriptionTitle'),
                subtitle: t('website.pricing.pluginRenewSubscriptionDescription'),
                confirmText: t('website.pricing.pluginConfirmRenewal'),
                cancelText: t('common.cancel'),
            });
        if (!confirmed) return;

        setIsActionLoading(true);
        try {
            logSentryEvent('payment.subscription.renewal_requested', {
                area: 'plugin_pricing',
                workspace_id: workspaceId,
                billing_cycle: PLUGIN_BILLING_CYCLE,
            });
            const response = await billingService.renewPluginSubscription({
                targetPlanId: scheduledPlan?.id,
                axiosRequestConfig: { headers: { 'X-Workspace-Id': workspaceId } },
            });

            if (!response?.success || response.paymentUrl) {
                throw new Error('Plugin renewal did not complete in Admin');
            }

            setPluginSubscriptionStatus(typeof response.status === 'string' ? response.status : 'active');
            if (!isSwitchingPlan) {
                setScheduledChange(null);
            }

            await Promise.all([
                userBilling.refreshAll(),
                refreshWorkspaces({ silent: true }),
                refreshPluginBillingStatus(),
            ]);
            showToast(
                isSwitchingPlan
                    ? t('website.pricing.planChangeScheduledToast')
                    : t('website.pricing.pluginSubscriptionRenewedToast'),
                'success',
            );
        } catch (error) {
            captureSentryError(error, {
                area: 'plugin_pricing',
                flow: 'renew_subscription',
                workspace_id: workspaceId,
            });
            showToast(t('website.pricing.pluginFailedToRenewSubscription'), 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const handlePrimaryAction = (selectedPlan: Plan, action: PluginPlanCardAction) => {
        if (isActionLoading) return;
        if (action === 'current-plan') return;
        if (action === 'renew') {
            void renewPluginSubscription();
            return;
        }
        if (action === 'renew-and-switch') {
            void renewPluginSubscription(selectedPlan);
            return;
        }
        void startPluginSubscription(selectedPlan, action);
    };

    const renderPlanCard = (plan: Plan) => {
        const scheduledButtonState = resolveScheduledPlanButtonState({
            scheduledChange: visibleScheduledChange,
            currentPlanCode,
            currentBillingCycle: PLUGIN_BILLING_CYCLE,
            planCode: plan.code,
            billingCycle: PLUGIN_BILLING_CYCLE,
        });
        const planAction = resolvePluginPlanCardAction({
            subscriptionStatus,
            currentPlanCode,
            targetPlanCode: plan.code,
            currentPlanPrice,
            targetPlanPrice: getPluginPlanPrice(plan),
        });
        const bubbleState = resolveScheduledPlanBubbleState({
            scheduledChange,
            currentPlanCode,
            currentBillingCycle: PLUGIN_BILLING_CYCLE,
            planCode: plan.code,
            billingCycle: PLUGIN_BILLING_CYCLE,
        });
        const scheduledEffectiveDate = formatScheduledEffectiveDate(bubbleState?.effectiveAt, locale);
        const pendingCancellationEffectiveDate = formatScheduledEffectiveDate(pluginSubscriptionCurrentPeriodEnd, locale);
        const isRenewalSwitchTarget = planAction === 'renew-and-switch';
        const planCardAction: PluginPlanCardAction = planAction;
        const renewalSwitchEffectiveDate = scheduledEffectiveDate || pendingCancellationEffectiveDate;
        const bubbleText = isRenewablePlan
            && isPluginCurrentPlan(plan, currentPlanCode)
            && pendingCancellationEffectiveDate
            ? t('website.pricing.currentPlanActiveUntil', {
                plan: plan.name,
                date: pendingCancellationEffectiveDate,
            })
            : isRenewalSwitchTarget && renewalSwitchEffectiveDate
            ? t('website.pricing.pluginRenewAndScheduledPlanTakesEffectOn', {
                plan: plan.name,
                date: renewalSwitchEffectiveDate,
            })
            : bubbleState && scheduledEffectiveDate
            ? bubbleState.kind === 'current-plan'
                ? t('website.pricing.currentPlanActiveUntil', {
                    plan: bubbleState.planLabel,
                    date: scheduledEffectiveDate,
                })
                : t('website.pricing.scheduledPlanTakesEffectOn', {
                    plan: bubbleState.planLabel,
                    date: scheduledEffectiveDate,
                })
            : null;
        const isPlanActionDisabled = planCardAction === 'current-plan'
            || Boolean(scheduledButtonState?.isDisabled)
            || isActionLoading
            || (!isRenewablePlan && !canOpenBillingPortal && plansState !== 'ready');
        const planActionLabel = scheduledButtonState
            ? getScheduledButtonLabel(scheduledButtonState.buttonText)
            : getPluginPlanActionLabel(planCardAction);
        const pluginPlanCard = buildPluginPricingPlanData({
            t,
            planName: plan.name,
            price: getPluginPlanPrice(plan),
            planFamily: getPluginPlanFamily(plan),
            memberLimit: getPluginPlanMemberLimit(plan),
            buttonText: planActionLabel,
            bubbleText,
        });

        return (
            <PricingCard
                key={plan.id}
                disabled={isPlanActionDisabled}
                fetching={plansState === 'loading'}
                loadingPlanName={isActionLoading ? pluginPlanCard.name : undefined}
                onAction={() => handlePrimaryAction(plan, planCardAction)}
                plan={pluginPlanCard}
            />
        );
    };

    return (
        <div className="mx-auto w-full max-w-[1140px]">
            <div className="grid w-full gap-5 lg:grid-cols-2">
                {pluginPlans.length > 0 ? pluginPlans.map(renderPlanCard) : (
                    <PricingCard
                        disabled={isActionDisabled}
                        fetching={plansState === 'loading'}
                        onAction={() => undefined}
                        plan={buildPluginPricingPlanData({
                            t,
                            buttonText: actionLabel,
                            bubbleText: plansState !== 'loading' ? t('website.pricing.pluginPlanUnavailable') : null,
                        })}
                    />
                )}
            </div>
            {canOpenBillingPortal ? (
                <BillingPortalLink
                    label={t('website.pricing.editBilling')}
                    onClick={() => { void openBillingPortal(); }}
                    disabled={isActionLoading}
                />
            ) : null}
        </div>
    );
};

export default PluginPlanSelector;
